import { AxiosError } from 'axios';
import { t } from 'i18next';
import { useState } from 'react';
import { useTheme } from 'styled-components';

import { authSettingsAPI } from '../../../../services/requests';

import { backendErrorNotification } from '../../../../components/Notification';
import * as s from '../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../enums/httpStatus';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { ApiResPaginated } from '../../../../types/apiResponseTypes';

import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import { AuthSetting } from '../../../../types/apiResponse/accessControl';
import AuthSettingUpInsertModal from './AuthSettingUpInsertModal';

const AuthSettingTSX = () => {
  const theme = useTheme();

  const [authSettings, setAuthSettings] = useState<AuthSetting[]>([]);
  const [authSettingsSelected, setAuthSettingsSelected] = useState<AuthSetting | null>(null);
  const [showUpInsertModal, setShowUpInsertModal] = useState(false);
  const [updateTable, setUpdateTable] = useState(0);

  const handleTableEditBtn = (row: AuthSetting) => {
    setAuthSettingsSelected(row);
    setShowUpInsertModal(true);
  };

  const actionsButtons = (row: AuthSetting) => (
    <s.ActionButtonsContainer>
      <s.EditIconTable onClick={() => handleTableEditBtn(row)} />
    </s.ActionButtonsContainer>
  );

  const getAuthSetting: RequestPage = async ({ search, page, quantityPerPage }) => {
    let hasMore = true;
    const source = authSettingsAPI.axios.CancelToken.source();

    try {
      authSettingsAPI.query = `?search=${search}&page=${page}&quantityPerPage=${quantityPerPage}`;
      const res = await authSettingsAPI.index(source.token);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      const { data, paginated }: ApiResPaginated<AuthSetting[]> = res.data;

      if (data.length === 0) {
        hasMore = false;
      }

      return {
        data,
        hasMore,
        totalPage: paginated.totalPage,
      };
    } catch (err) {
      if (!authSettingsAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return {
        data: [],
        hasMore: false,
        totalPage: 0,
      };
    }
  };

  const handleActiveColumn = (row: AuthSetting) => {
    if (row.active) {
      return <p style={{ color: theme.colors.positive }}>{t('yes') as string}</p>;
    }
    return <p style={{ color: theme.colors.negative }}>{t('not') as string}</p>;
  };

  const columns: Column<AuthSetting>[] = [
    {
      label: t('name') as string,
      accessor: 'auth_type_name',
    },
    {
      label: t('active') as string,
      Cell: handleActiveColumn,
    },
    {
      Cell: actionsButtons,
    },
  ];

  const onClickNewBtn = () => {
    setAuthSettingsSelected(null);
    setShowUpInsertModal(true);
  };

  const handleUpInsertModalCallback = () => {
    setUpdateTable(updateTable + 1);
    setShowUpInsertModal(false);
    setAuthSettingsSelected(null);
  };

  const handleUpInsertModalOnClose = () => {
    setShowUpInsertModal(false);
    setAuthSettingsSelected(null);
  };

  return (
    <ContentContainer title={t('authSettings')}>
      <NewRegisterBtn
        onClick={() => onClickNewBtn()}
        style={{ marginBottom: '1rem', marginLeft: '0' }}
      />

      <AsyncTable
        tableName={t('authSettings')}
        columns={columns}
        value={authSettings}
        onChange={setAuthSettings}
        requestPage={getAuthSetting}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
        }}
      />

      {showUpInsertModal && (
        <AuthSettingUpInsertModal
          showModal={showUpInsertModal}
          closeModal={() => handleUpInsertModalOnClose()}
          authSettingSelected={authSettingsSelected}
          callback={() => handleUpInsertModalCallback()}
        />
      )}
    </ContentContainer>
  );
};

export default AuthSettingTSX;
